import React, { useRef, useState } from 'react';
import { X, Printer, Download, Loader2, AlertCircle, CheckCircle2, Unlock } from 'lucide-react';
import { ReceiptTicket } from './ReceiptTicket';
import type { ReceiptData } from '../../utils/receipt';
import { downloadReceiptPdf } from '../../utils/receipt';
import { Modal } from '../ui';
import { api } from '../../utils/api';

interface ReceiptPreviewModalProps {
  data: ReceiptData | null;
  onClose: () => void;
}

export function ReceiptPreviewModal({ data, onClose }: ReceiptPreviewModalProps) {
  const ticketRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  const [openingDrawer, setOpeningDrawer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [drawerSuccess, setDrawerSuccess] = useState(false);

  if (!data) return null;

  const handlePrint = async () => {
    if (!data?.orderId) {
      setError('No se puede imprimir: falta el ID de la orden.');
      return;
    }

    setPrinting(true);
    setError(null);
    setSuccess(false);

    try {
      await api.post('/print-jobs/enqueue', {
        order_id: data.orderId,
        type: data.isPreReceipt ? 'pre_receipt' : 'receipt',
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1800);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Error al encolar la impresión.';
      setError(msg);
    } finally {
      setPrinting(false);
    }
  };

  const handleOpenDrawer = async () => {
    if (!data?.orderId) {
      setError('No se puede abrir la caja: falta el ID de la orden.');
      return;
    }

    setOpeningDrawer(true);
    setError(null);
    setDrawerSuccess(false);

    try {
      await api.post('/print-jobs/enqueue', {
        order_id: data.orderId,
        type: 'drawer',
      });

      setDrawerSuccess(true);
      setTimeout(() => {
        setDrawerSuccess(false);
      }, 1800);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Error al enviar comando de apertura.';
      setError(msg);
    } finally {
      setOpeningDrawer(false);
    }
  };

  const handleDownload = () => {
    downloadReceiptPdf(data);
  };

  return (
    <>

      <Modal open={!!data} onClose={onClose} titleId="receipt-preview-title" className="max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 id="receipt-preview-title" className="text-base font-semibold text-text">
            {data.isPreReceipt ? 'Vista previa de pre-cuenta' : 'Vista previa del ticket'}
          </h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted/50 transition"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Ticket preview */}
        <div className="overflow-y-auto max-h-[60vh] px-4 py-4 flex justify-center bg-gray-100">
          <div
            id="receipt-preview-print"
            className="shadow-lg"
            style={{ background: 'white', border: '1px solid #ddd' }}
          >
            <ReceiptTicket ref={ticketRef} data={data} />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle size={15} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3 border-t border-border px-5 py-4">
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              disabled={printing || success}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:opacity-60 ${
                success
                  ? 'bg-emerald-500 text-white'
                  : 'bg-primary text-white hover:opacity-90'
              }`}
            >
              {printing ? (
                <><Loader2 size={16} className="animate-spin" /> Enviando…</>
              ) : success ? (
                <><CheckCircle2 size={16} /> ¡Enviado!</>
              ) : (
                <><Printer size={16} /> {data.isPreReceipt ? 'Imprimir pre-cuenta' : 'Imprimir'}</>
              )}
            </button>

            {!data.isPreReceipt && (
              <button
                onClick={handleOpenDrawer}
                disabled={openingDrawer || drawerSuccess}
                className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:opacity-60 border ${
                  drawerSuccess
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20 hover:bg-amber-50'
                }`}
              >
                {openingDrawer ? (
                  <><Loader2 size={16} className="animate-spin" /> Abriendo…</>
                ) : drawerSuccess ? (
                  <><CheckCircle2 size={16} /> ¡Abierto!</>
                ) : (
                  <><Unlock size={16} /> Abrir caja</>
                )}
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-text hover:bg-muted/30 transition"
            >
              <Download size={16} /> Descargar PDF
            </button>
            <button
              onClick={onClose}
              className="flex flex-1 items-center justify-center rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-text-muted hover:bg-muted/30 transition"
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default ReceiptPreviewModal;

